import DashboardIcon from "@mui/icons-material/Dashboard";
import { Button, Grid, Typography } from "@mui/material";
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  defaults,
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import ChartProvider from ".";
import { transparentize } from "../../../middleware/middleware";
import LoadingWrapper from "../loading";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  zoomPlugin,
  Filler
);

const LineChartCustom = ({
  title,
  xAxis = "x",
  xType = undefined,
  xAxisTitle = "",
  yAxisTitle = "",
  data = [],
  series = [],
  loading = false,
  colors = [],
  showZoomOutButton = false,
  className = "",
  fill = false,
  opacity = 0.2,
  multiScale = null,
  pointStyle = null,
  customTooltips = [],
  isDashboardView = false,
  yAxisMax = null,
  yAxisMin = null,
}) => {
  defaults.font.family = "dana FD";

  const [state, setState] = useState({
    labels: [],
    datasets: [],
  });

  useEffect(() => {
    if (Array.isArray(data) && data.length > 0) {
      const labels = data.map((item) => item[xAxis]);

      const datasets = series.map((sere, index) => ({
        label: sere,
        data: data.map((item) => item[sere]),
        borderColor: ChartProvider.renderColor(colors, index),
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 400);
          gradient.addColorStop(
            0,
            transparentize(ChartProvider.renderColor(colors, index), opacity)
          );
          gradient.addColorStop(
            1,
            transparentize(ChartProvider.renderColor(colors, index), 0.04)
          );
          return gradient;
        },
        fill: fill ? "origin" : false,
        ...(multiScale?.length ? { yAxisID: multiScale[index] ?? "y" } : {}),
        ...(pointStyle?.length
          ? { pointStyle: pointStyle?.[index] ? pointStyle[index] : false }
          : {}),
      }));

      setState((old) => ({
        ...old,
        labels,
        datasets,
      }));
    }
  }, [data]);

  const onRenderFooter = (tooltipItems) => {
    // console.log("tooltipItems", tooltipItems);
    if (tooltipItems?.length) {
      const { dataIndex } = tooltipItems[0];
      if (customTooltips?.[dataIndex]) {
        return customTooltips[dataIndex];
      }
    }
    return "";
  };

  const scales = {
    x: {
      type: "category",
      grid: {
        borderColor: "rgba( 0, 0, 1)",
        color: "rgba( 0, 0, 0, 0.1)",
      },
      title: {
        display: true,
        text: xAxisTitle,
      },
    },
    y: {
      type: "linear",
      ticks: {
        callback: (val, index, ticks) =>
          index === 0 || index === ticks.length - 1 ? null : val,
      },
      grid: {
        borderColor: "rgba( 0, 0, 1)",
        color: "rgba( 0, 0, 0, 0.1)",
      },
      title: {
        display: true,
        text: yAxisTitle,
      },
      ...(typeof yAxisMin === "number" ? { min: yAxisMin } : {}),
      ...(typeof yAxisMax === "number" ? { max: yAxisMax } : {}),
    },
    ...(multiScale
      ? {
          y1: {
            type: "linear",
            display: true,
            position: "right",
            grid: {
              drawOnChartArea: false, // only want the grid lines for one axis to show up
            },
            // grid: {
            //   borderColor: "rgba( 0, 0, 1)",
            //   color: "rgba( 0, 0, 0, 0.1)",
            // },
          },
        }
      : {}),
  };

  const options = {
    responsive: true,
    scales: scales,
    interaction: {
      mode: "index",
      intersect: false,
    },
    stacked: false,
    // aspectRatio: 1.5,
    maintainAspectRatio: isDashboardView ? false : true,
    plugins: {
      zoom: {
        pan: {
          enabled: true,
          mode: "x",
          modifierKey: "ctrl",
        },
        zoom: {
          drag: {
            enabled: true,
          },
          wheel: {
            enabled: true, // SET SCROOL ZOOM TO TRUE
            modifierKey: "ctrl",
          },
          mode: "x",
        },
      },
      ...(customTooltips?.length
        ? {
            tooltip: {
              callbacks: {
                footer: onRenderFooter,
              },
            },
          }
        : {}),
    },
  };

  return (
    <Grid
      container
      style={{ direction: "rtl", minHeight: 288 }}
      className={clsx(className)}
    >
      {loading ? (
        <Grid
          item
          xs={12}
          md={12}
          container
          justifyContent="center"
          alignItems="center"
        >
          <LoadingWrapper />
        </Grid>
      ) : Array.isArray(data) && data.length > 0 ? (
        <Grid item xs={12} md={12} style={{ position: "relative" }}>
          <Typography textAlign={"center"} variant="h6" fontWeight={"bold"}>
            {title}
          </Typography>

          {showZoomOutButton && (
            <Button
              onClick={() => {
                setState((old) => ({ ...old }));
              }}
              variant="outlined"
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                fontSize: 8,
              }}
            >
              Zoom Out
            </Button>
          )}

          <Line options={options} data={state} />
        </Grid>
      ) : (
        <Grid
          item
          xs={12}
          md={12}
          container
          justifyContent="center"
          alignItems="center"
          direction="column"
        >
          <DashboardIcon sx={{ fontSize: "8rem", mb: 3 }} color="gray" />
          <Typography color="gray" variant="h6" fontWeight="bold">
            داده ای جهت نمایش وجود ندارد
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};

export default LineChartCustom;
